import React from 'react';
import { CharacterToken as CharacterTokenType, Position, GridType } from '../types';
import { inchesToPixels, TOKEN_RADIUS, GRID_CENTER_OFFSET } from '../data/mapConstants';

// 6 hex neighbor directions in axial (q, r) for the hidden double-hex token
const HEX_DIRS = [
  { dq: 1, dr: 0 },   // 0: East
  { dq: 1, dr: -1 },  // 1: Northeast
  { dq: 0, dr: -1 },  // 2: Northwest
  { dq: -1, dr: 0 },  // 3: West
  { dq: -1, dr: 1 },  // 4: Southwest
  { dq: 0, dr: 1 },   // 5: Southeast
];

interface CharacterTokenProps {
  token: CharacterTokenType;
  onMouseDown?: (token: CharacterTokenType, e: React.MouseEvent) => void;
  isDragging?: boolean;
  selectingPlayers?: Array<1 | 2 | 'observer'>; // Player numbers who have selected this character
  /** Function to convert grid position to pixel coordinates */
  positionToPixels?: (position: Position) => { x: number; y: number };
  /** Grid type - affects positioning calculations */
  gridType?: GridType;
}

const CharacterToken: React.FC<CharacterTokenProps> = ({
  token,
  onMouseDown,
  isDragging,
  selectingPlayers = [],
  positionToPixels,
  gridType = 'square'
}) => {
  // Calculate pixel position based on grid type
  let x: number, y: number;
  if (positionToPixels && gridType === 'hex') {
    // Hex grid: use provided converter (axial coords -> pixels, centered on hex)
    const pixels = positionToPixels(token.position);
    x = pixels.x;
    y = pixels.y;
  } else {
    // Square grid: position is in inches, add offset to center in cell
    x = inchesToPixels(token.position.x + GRID_CENTER_OFFSET);
    y = inchesToPixels(token.position.y + GRID_CENTER_OFFSET);
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMouseDown?.(token, e);
  };

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click from bubbling to SVG (which would clear selection)
    e.stopPropagation();
  };

  // Determine opacity and filter based on status
  const getTokenOpacity = () => {
    if (token.state === 'Unconscious') return 0.3;
    if (token.exhausted) return 0.5;
    return 1;
  };

  const getTokenFilter = () => {
    if (token.state === 'Unconscious') return 'grayscale(1)';
    if (token.exhausted) return 'grayscale(0.7)';
    return 'none';
  };

  // Get focus ring color based on player number
  const getFocusRingColor = (playerNumber: 1 | 2 | 'observer'): string => {
    if (playerNumber === 1) return '#3b82f6'; // blue-500 (Player 1 controls Blue team)
    if (playerNumber === 2) return '#ef4444'; // red-500 (Player 2 controls Red team)
    return '#eab308'; // yellow-500 for observers
  };

  // Double-hex capsule for hidden tokens on a hex grid
  const isDoubleHex = token.state === 'Hidden' && gridType === 'hex' && positionToPixels;
  let x2 = x, y2 = y;
  if (isDoubleHex) {
    const dir = HEX_DIRS[(token.hiddenRotation ?? 0) % 6];
    const neighbor = { x: token.position.x + dir.dq, y: token.position.y + dir.dr };
    const np = positionToPixels(neighbor);
    x2 = np.x;
    y2 = np.y;
  }

  const renderDoubleHex = () => {
    if (!isDoubleHex) return null;
    const vx = x2 - x, vy = y2 - y;
    const len = Math.sqrt(vx * vx + vy * vy);
    if (len === 0) return null;
    const ux = vx / len, uy = vy / len;
    const px = -uy, py = ux;
    const r = TOKEN_RADIUS;
    // Single capsule path: right side near → right side far → arc far cap → left side far → left side near → arc near cap
    const ax = x + px * r,  ay = y + py * r;
    const bx = x2 + px * r, by = y2 + py * r;
    const cx = x2 - px * r, cy = y2 - py * r;
    const ex = x - px * r,  ey = y - py * r;
    const pathD = `M ${ax},${ay} L ${bx},${by} A ${r},${r} 0 0,0 ${cx},${cy} L ${ex},${ey} A ${r},${r} 0 0,0 ${ax},${ay} Z`;
    const mx = (x + x2) / 2, my = (y + y2) / 2;
    return (
      <g>
        <path d={pathD} fill={token.color} stroke="#fff" strokeWidth={2} opacity={0.9} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
        <text x={mx} y={my + 4} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold" pointerEvents="none" style={{ userSelect: 'none' }}>
          {token.role[0]}
        </text>
      </g>
    );
  };

  return (
    <g
      className={`cursor-move ${isDragging ? 'opacity-70 no-transition' : ''}`}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      opacity={getTokenOpacity()}
      style={{
        filter: getTokenFilter(),
      }}
    >
      {/* Focus rings */}
      {selectingPlayers.map((playerNumber, index) => (
        isDoubleHex ? (
          // Elongated ring: two circles for double-hex
          <g key={`focus-ring-${playerNumber}-${index}`}>
            <circle cx={x} cy={y} r={TOKEN_RADIUS + 4 + index * 3} fill="none" stroke={getFocusRingColor(playerNumber)} strokeWidth={2} opacity={0.8} />
            <circle cx={x2} cy={y2} r={TOKEN_RADIUS + 4 + index * 3} fill="none" stroke={getFocusRingColor(playerNumber)} strokeWidth={2} opacity={0.8} />
          </g>
        ) : (
          <circle
            key={`focus-ring-${playerNumber}-${index}`}
            cx={x}
            cy={y}
            r={TOKEN_RADIUS + 4 + (index * 3)}
            fill="none"
            stroke={getFocusRingColor(playerNumber)}
            strokeWidth={2}
            opacity={0.8}
          />
        )
      ))}

      {isDoubleHex ? renderDoubleHex() : (
        <>
          {/* Main token circle */}
          <circle
            cx={x}
            cy={y}
            r={TOKEN_RADIUS}
            fill={token.color}
            stroke="#fff"
            strokeWidth={2}
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))' }}
          />
          {/* Token role label (first letter) */}
          <text
            x={x}
            y={y + 4}
            textAnchor="middle"
            fill="#fff"
            fontSize="12"
            fontWeight="bold"
            pointerEvents="none"
            style={{ userSelect: 'none' }}
          >
            {token.role[0]}
          </text>
        </>
      )}
    </g>
  );
};

export default CharacterToken;
